import React, { useCallback, useRef } from "react";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { usePrimaryOrganization } from "../../hooks";
import { usePrivacyCenter } from "../../hooks/usePrivacyCenter";
import {
  PrivacyCenterThemeForm,
  PrivacyCenterThemeFormHandle,
} from "../../components/privacyCenter/PrivacyCenterThemeForm";
import { ProgressFooter, ProgressFooterActions } from "../get-compliant/ProgressFooter";
import {
  useEditPrivacyCenter,
  useUpdateNoticeApi,
} from "../../components/privacyCenter/EditPrivacyCenter";
import { useHistory } from "react-router";
import { Routes } from "../../root/routes";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../stay-compliant/PrivacyNoticeChecklistItems";
import { DevInstructionsPoliciesAndNoticesOnboardingSlug } from "../stay-compliant/privacy-center/DSARForms";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { didFinishPoliciesAndNoticesOnboarding } from "../../../common/hooks/useOnboardingProgress";

export const PrivacyCenterTheme = ({
  setShowingCustomBanner,
}: {
  setShowingCustomBanner: (value: boolean) => void;
}) => {
  const history = useHistory();

  const [org] = usePrimaryOrganization();
  const [privacyCenter, , fetch] = usePrivacyCenter(org?.id);

  const { answers: developerInstructionsAnswers, setAnswer: setDevInstructionsAnswer } = useSurvey(
    org?.id,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );

  const editPrivacyCenter = useEditPrivacyCenter();

  const hasPublishedNotice = editPrivacyCenter?.props?.latestPublishedNoticeUpdatedOn != null;

  const hasNotFinishedTheme = !didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers);

  const showOnboardingUi = !hasPublishedNotice || hasNotFinishedTheme;

  const { updateNotice } = useUpdateNoticeApi();

  // Callback to publish the notice and refresh data so hasPublishedNotice updates
  const handlePublishNotice = useCallback(async () => {
    const draftNotice = editPrivacyCenter?.props?.privacyCenter?.notices?.find(
      (notice) => notice.type === "PRIVACY_POLICY" && notice.status === "DRAFT",
    );
    if (draftNotice && editPrivacyCenter?.props?.privacyCenter?.id) {
      await updateNotice({
        organizationId: org?.id,
        privacyCenterId: editPrivacyCenter.props.privacyCenter.id,
        noticeId: draftNotice.publicId,
        updateNoticeBody: {
          status: "PUBLISHED",
        },
      });
      // Refresh the notices list so hasPublishedNotice updates
      await editPrivacyCenter.props.allNoticesRefresh();
    }
  }, [org, editPrivacyCenter, updateNotice]);

  // Ref that the child form component will initialize with a submit handler (so we can call the form's submit method from this parent component)
  const publishThemeChangesRef = useRef<PrivacyCenterThemeFormHandle>();

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        showOnboardingUi && (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent="Done with Themes"
                nextDisabled={!hasPublishedNotice}
                nextDisabledTooltip={
                  !hasPublishedNotice
                    ? "Please publish your Privacy Center before proceeding."
                    : undefined
                }
                nextOnClick={async () => {
                  if (publishThemeChangesRef.current) {
                    await publishThemeChangesRef.current.submitForm();
                  }
                  // Mark Policies & Notices section as complete
                  await setDevInstructionsAnswer(
                    DevInstructionsPoliciesAndNoticesOnboardingSlug,
                    "true",
                  );
                  history.push(Routes.privacyCenter.dsarForms);
                }}
              />
            }
          />
        )
      }
    >
      <PrivacyCenterThemeForm
        ref={publishThemeChangesRef}
        org={org}
        privacyCenter={privacyCenter}
        onUpdate={fetch}
        includeUiShell={true}
        draftNoticeId={editPrivacyCenter?.props?.draftPrivacyPolicyNoticeId}
        hasPublishedNotice={hasPublishedNotice}
        // Since in the onboarding UI we publish the changes when the user clicks Next, we don't want to show the top banner for unpublished changes
        showTopBannerForUnpublishedChanges={!showOnboardingUi}
        setShowingCustomBanner={setShowingCustomBanner}
        onPublishNotice={handlePublishNotice}
      />
    </StandardPageWrapper>
  );
};
